// CSV export of a Fragen-Check: a metadata block (the check, its page, its
// progress, what the figures are), then one row per checked question in the
// pasted order, every key in words. Downloaded through `downloadCsv`
// (`lib/export.ts`), so Excel opens it with Swiss umlauts intact; the query
// and answer columns go through `csvCell`, which keeps a formula-like cell text.

import type { CheckResult, QuestionCheck, QuestionGroup } from "@/api/types";
import { t, type TranslationKey } from "@/i18n";
import { csvRow, downloadCsv, yesNo } from "@/lib/export";
import { formatDateTime } from "@/lib/format";
import {
  type GroupNames,
  groupNamesOf,
  progressText,
  qtypeLabel,
  resultErrorLabel,
  verdictLabel,
} from "@/lib/questionCheck";

export interface CheckExport {
  check: QuestionCheck;
  results: readonly CheckResult[];
  /** The checked list's groups; a group they lack reads as its key. */
  groups: readonly QuestionGroup[];
}

// Machine-read by the spreadsheet: a period, no thousands separator. A
// question costs a fraction of a cent, hence four places.
const money = (usd: number): string => usd.toFixed(4);

// The accessor parameter is `result`, never `t`, which translates.
const COLUMNS: Array<[TranslationKey, (result: CheckResult, names: GroupNames) => unknown]> = [
  ["export.col.nr", (result) => result.n + 1],
  ["export.col.question", (result) => result.query],
  ["qc.export.col.group", (_result, names) => names.label],
  ["qc.export.col.kind", (_result, names) => names.kind],
  ["qc.export.col.qtype", (result) => qtypeLabel(result.qtype)],
  ["qc.export.col.clicks", (result) => result.clicks],
  ["qc.export.col.impressions", (result) => result.impressions],
  ["qc.export.col.verdict", (result) => verdictLabel(result.verdict)],
  ["qc.export.col.listings", (result) => yesNo(result.listings)],
  ["qc.export.col.bestUrl", (result) => result.best_url],
  ["qc.export.col.bestTitle", (result) => result.best_title],
  ["qc.export.col.score", (result) => (result.best_score === null ? "" : result.best_score.toFixed(2))],
  ["export.col.answer", (result) => result.answer],
  ["export.col.searchQuery", (result) => result.search_query],
  ["export.col.docsFound", (result) => result.docs_returned],
  ["export.col.pagesForwarded", (result) => result.chunks_used],
  ["export.col.sourcesCited", (result) => result.sources_cited],
  ["export.col.latency", (result) => result.latency_ms],
  ["export.col.model", (result) => result.model],
  ["qc.export.col.costUsd", (result) => money(result.cost_usd)],
  ["qc.export.col.error", (result) => (result.error ? resultErrorLabel(result.error) : "")],
  ["sessions.meta.id", (result) => result.session_id],
  ["export.col.turnId", (result) => result.turn_id],
];

function metadataRows(check: QuestionCheck): Array<[string, string]> {
  return [
    [t("qc.export.meta.check"), check.name || t("qc.unnamed")],
    [t("qc.export.meta.created"), formatDateTime(check.created_at)],
    [t("qc.export.meta.by"), check.created_by],
    [t("qc.export.meta.page"), check.page],
    [t("qc.export.meta.progress"), progressText(check)],
    [t("qc.export.meta.cost"), money(check.cost_usd)],
    [t("qc.export.meta.basis"), t("qc.export.meta.basisNote")],
  ];
}

/** The sheet: the metadata block, a blank line, the header row, one row per
 * question in the pasted order. Headers and words follow the dashboard language. */
export function buildCheckCsv({ check, results, groups }: CheckExport): string {
  const byKey = new Map(groups.map((group) => [group.key, group]));
  const lines = metadataRows(check).map(csvRow);
  lines.push("");
  lines.push(csvRow(COLUMNS.map(([header]) => t(header))));
  for (const result of [...results].sort((a, b) => a.n - b.n)) {
    const names = groupNamesOf(result.group, byKey);
    lines.push(csvRow(COLUMNS.map(([, get]) => get(result, names))));
  }
  return lines.join("\r\n");
}

/** `fragen-check-<check id>.csv`, the same in both languages so a sheet is found again. */
export function checkCsvFilename(check: QuestionCheck): string {
  return `fragen-check-${check.id}.csv`;
}

export function downloadCheckCsv(data: CheckExport): void {
  downloadCsv(checkCsvFilename(data.check), buildCheckCsv(data));
}
